import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CustomRequestStore } from "@/stores/customRequestStore";
import {
  X,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  ChefHat,
  CalendarDays,
  MapPin,
  Users,
  Wallet,
  UtensilsCrossed,
  MessageCircle,
  IdCard,
  CreditCard,
  Clock,
  ShieldCheck,
  Check,
} from "lucide-react";
import { Image } from "@unpic/react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useCustomRequestForm } from "@/hooks/useCustomRequestForm";
import { Step1ChefType } from "@/components/forms/custom-request/Step1ChefType";
import { Step2DateTime } from "@/components/forms/custom-request/Step2DateTime";
import { Step3Location } from "@/components/forms/custom-request/Step3Location";
import { Step4GuestCount } from "@/components/forms/custom-request/Step4GuestCount";
import { Step5Budget } from "@/components/forms/custom-request/Step5Budget";
import { Step6Preferences } from "@/components/forms/custom-request/Step6Preferences";
import { Step7Message } from "@/components/forms/custom-request/Step7Message";
import { Step8Contact } from "@/components/forms/custom-request/Step8Contact";
import { motion, AnimatePresence } from "motion/react";

const STEPS = [
  { label: "Type", icon: ChefHat },
  { label: "Date", icon: CalendarDays },
  { label: "Lieu", icon: MapPin },
  { label: "Convives", icon: Users },
  { label: "Budget", icon: Wallet },
  { label: "Goûts", icon: UtensilsCrossed },
  { label: "Message", icon: MessageCircle },
  { label: "Contact", icon: IdCard },
];

const getEncouragement = (current: number, total: number) => {
  const remaining = total - current - 1;
  if (current === 0) return "On démarre — quelques minutes suffisent ✨";
  if (current === total - 1) return "Dernière étape — vous y êtes !";
  if (remaining === 1) return "Plus qu'une étape, courage !";
  if (current >= total / 2) return `Plus que ${remaining} étapes — continuez !`;
  return "Bien joué, on continue !";
};

export const CustomRequestDialog = () => {
  const { isOpen, setOpen } = CustomRequestStore();
  const { form, currentStep, nextStep, prevStep, isFirstStep, isLastStep, onSubmit } =
    useCustomRequestForm();

  const handleClose = () => setOpen(false);

  const progressPct = Math.round(((currentStep + 1) / STEPS.length) * 100);
  const remainingSteps = STEPS.length - currentStep - 1;
  const estimatedMin = Math.max(1, Math.ceil(remainingSteps * 0.4));

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return <Step1ChefType />;
      case 1: return <Step2DateTime />;
      case 2: return <Step3Location />;
      case 3: return <Step4GuestCount />;
      case 4: return <Step5Budget />;
      case 5: return <Step6Preferences />;
      case 6: return <Step7Message />;
      case 7: return <Step8Contact />;
      default: return <Step1ChefType />;
    }
  };

  const CurrentIcon = STEPS[currentStep].icon;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose} modal={true}>
      <DialogContent className="!max-w-none w-screen h-dvh p-0 gap-0 border-0 bg-[#F8F7F5] [&>button]:hidden flex flex-col overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <DialogHeader className="flex-none bg-white border-b border-border px-6 max-md:px-4 py-3 flex flex-row items-center justify-between">
          <DialogTitle>
            <Image
              src="/logo.svg"
              alt="Hometoq"
              layout="constrained"
              width={120}
              height={18}
              className="h-7 w-auto"
            />
          </DialogTitle>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-gray-50 px-2.5 py-1 rounded-full">
              <Clock size={13} className="text-primary" />
              <span className="tabular-nums">~{estimatedMin} min restantes</span>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Fermer"
            >
              <X size={20} className="text-dark-blue" strokeWidth={2.5} />
            </button>
          </div>
        </DialogHeader>

        {/* ── Progress section ───────────────────────────────────────────── */}
        <div className="flex-none bg-white border-b border-border px-6 max-md:px-4 pt-4 pb-5">
          <div className="max-w-2xl mx-auto space-y-2.5">
            {/* Top row: current step + percentage */}
            <div className="flex items-end justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <CurrentIcon size={16} className="text-primary" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold text-primary uppercase tracking-wider leading-none mb-0.5">
                    Étape {currentStep + 1} / {STEPS.length}
                  </p>
                  <p className="text-sm font-semibold text-dark-blue truncate">
                    {STEPS[currentStep].label}
                  </p>
                </div>
              </div>
              <div className="flex items-baseline gap-0.5 shrink-0">
                <motion.span
                  key={progressPct}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-xl font-bold text-primary tabular-nums leading-none"
                >
                  {progressPct}
                </motion.span>
                <span className="text-xs font-bold text-primary leading-none">%</span>
              </div>
            </div>

            {/* Animated progress bar with milestone dots */}
            <div className="relative h-2 bg-gray-100 rounded-full overflow-visible">
              <motion.div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/85 rounded-full"
                initial={false}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              />
              {/* Milestones */}
              <div className="absolute inset-0 flex justify-between items-center pointer-events-none">
                {STEPS.map((_, i) => {
                  const done = i < currentStep;
                  const active = i === currentStep;
                  return (
                    <motion.div
                      key={i}
                      initial={false}
                      animate={{
                        scale: active ? 1.4 : 1,
                      }}
                      transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                        done
                          ? "bg-white ring-2 ring-primary"
                          : active
                            ? "bg-white ring-2 ring-primary shadow-sm"
                            : "bg-gray-200 ring-2 ring-white"
                      }`}
                    >
                      {done && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500, damping: 25 }}
                          className="w-full h-full flex items-center justify-center"
                        >
                          <Check size={6} strokeWidth={4} className="text-primary" />
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Encouragement message */}
            <AnimatePresence mode="wait">
              <motion.p
                key={currentStep}
                initial={{ opacity: 0, y: -3 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 3 }}
                transition={{ duration: 0.25 }}
                className="flex items-center gap-1.5 text-xs text-muted-foreground italic"
              >
                <Sparkles size={11} className="text-primary shrink-0" />
                <span className="truncate">{getEncouragement(currentStep, STEPS.length)}</span>
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* ── Content ────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full flex flex-col">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex-1 w-full max-w-2xl mx-auto px-6 max-md:px-4 py-8 max-md:py-6"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                  >
                    {renderCurrentStep()}
                  </motion.div>
                </AnimatePresence>
              </form>
            </Form>
          </div>
        </div>

        {/* ── Trust bar ──────────────────────────────────────────────────── */}
        <div className="flex-none bg-primary/8 border-t border-primary/15 py-2.5 flex justify-center items-center gap-8 max-md:gap-4 px-4">
          {[
            { icon: <Clock size={14} />, label: "Réponse en moins d'1h" },
            { icon: <ShieldCheck size={14} />, label: "Sans engagement" },
            { icon: <CreditCard size={14} />, label: "Paiement sécurisé" },
          ].map(({ icon, label }) => (
            <p key={label} className="flex items-center gap-1.5 text-xs text-dark-blue font-medium">
              <span className="text-primary">{icon}</span>
              <span className="max-md:hidden">{label}</span>
            </p>
          ))}
        </div>

        {/* ── Footer nav ─────────────────────────────────────────────────── */}
        <div className="flex-none bg-white border-t border-border px-6 max-md:px-4 py-3 flex items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={prevStep}
            disabled={isFirstStep}
            className="flex items-center gap-2 font-button-large py-5 px-8 max-md:px-5 text-primary border-primary hover:bg-primary/8 hover:text-primary disabled:opacity-30"
          >
            <ArrowLeft size={16} />
            <span className="max-md:hidden">Précédent</span>
          </Button>

          {/* Pill dot indicator */}
          <div className="hidden sm:flex items-center gap-1.5">
            {STEPS.map((_, i) => {
              const done = i < currentStep;
              const active = i === currentStep;
              return (
                <motion.div
                  key={i}
                  initial={false}
                  animate={{
                    width: active ? 24 : 6,
                  }}
                  transition={{ type: "spring", stiffness: 350, damping: 28 }}
                  className={`h-1.5 rounded-full transition-colors duration-300 ${
                    done ? "bg-primary" : active ? "bg-primary" : "bg-gray-200"
                  }`}
                />
              );
            })}
          </div>

          <Button
            type="button"
            onClick={isLastStep ? form.handleSubmit(onSubmit) : nextStep}
            className="flex items-center gap-2 font-button-large py-5 px-8 max-md:px-5 shadow-sm shadow-primary/20"
          >
            <span>{isLastStep ? "Envoyer ma demande" : "Suivant"}</span>
            {!isLastStep && <ArrowRight size={16} />}
            {isLastStep && <Sparkles size={16} />}
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
};
